import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import EvaluationAnswersReview, {
    QuestionDetail,
} from '@/components/evaluation/EvaluationAnswersReview';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import RankBadge from '@/components/RankBadge';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Evaluation } from '@/types';
import { router } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ResultsData {
    score: number;
    total_score: number;
    percentage: number;
    passed: boolean;
    correct_answers: number;
    incorrect_answers: number;
    unanswered_questions: number;
    total_questions: number;
    time_taken_minutes: number;
    submitted_at: string;
}

interface PlacementData {
    placed_rank: { id: number; name: string };
    seeded_points: number;
}

interface ResultsProps {
    evaluation: Evaluation;
    results: ResultsData;
    questions: QuestionDetail[];
    can_retake: boolean;
    next_retake_time: string | null;
    course: {
        slug: string;
    } | null;
    has_certificate_template: boolean;
    isPlacement?: boolean;
    placement?: PlacementData | null;
}

const ResultsContent: React.FC<ResultsProps> = ({
    evaluation,
    results,
    questions,
    can_retake,
    next_retake_time,
    course,
    has_certificate_template,
    isPlacement = false,
    placement = null,
}) => {
    const { t } = useTranslation();
    const pageTitle = `Resultados - ${evaluation.title}`;

    const formatTime = (minutes: number) => {
        if (minutes < 60) {
            return t('evaluation.results.minutesFormat', {
                count: Math.round(minutes),
            });
        }

        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);

        if (remainingMinutes === 0) {
            return t('evaluation.results.hoursFormat', { count: hours });
        }

        return t('evaluation.results.hoursMinutesFormat', {
            hours,
            minutes: remainingMinutes,
        });
    };

    const getScoreColor = () => {
        if (results.percentage >= 80) return 'text-green-600';
        if (results.percentage >= 60) return 'text-yellow-600';
        return 'text-red-600';
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const handleReturnToCourse = () => {
        if (course) {
            router.get(`/courses/${course.slug}/`);
        }
    };

    const handleContinueToCourses = () => {
        router.get('/');
    };

    const handleRetakeEvaluation = () => {
        router.get(`/evaluations/${evaluation.id}/`);
    };

    const handleViewCertificate = () => {
        router.get(`/evaluations/${evaluation.id}/certificate/`);
    };

    const formatRetakeTime = () => {
        if (!next_retake_time) return '';

        const retakeDate = new Date(next_retake_time);
        const now = new Date();
        const timeLeft = retakeDate.getTime() - now.getTime();

        if (timeLeft <= 0) return '';

        const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
        const minutesLeft = Math.floor(
            (timeLeft % (1000 * 60 * 60)) / (1000 * 60)
        );

        if (hoursLeft > 0) {
            return `${hoursLeft}h ${minutesLeft}m`;
        }
        return `${minutesLeft}m`;
    };

    return (
        <AppLayout>
            <PageTitle title={pageTitle} />
            <div className="flex min-h-screen items-center justify-center px-4 py-8">
                <GlassCard className="w-full max-w-3xl overflow-hidden">
                    {/* Header */}
                    <div className="border-b border-white/30 px-8 py-6 dark:border-white/10">
                        <div className="text-center">
                            <h1 className="mb-2 text-2xl font-bold text-gray-800 dark:text-white">
                                {t('evaluation.results.title')}
                            </h1>
                            <p className="text-gray-600 dark:text-neutral-300">
                                {evaluation.title}
                            </p>
                        </div>
                    </div>

                    {/* Placement Result */}
                    {isPlacement && (
                        <div className="border-b border-white/30 bg-indigo-50 px-8 py-8 dark:border-white/10 dark:bg-indigo-950/30">
                            <div className="text-center">
                                <p className="mb-4 text-gray-700 dark:text-neutral-200">
                                    {t('evaluation.placement.placedAt')}
                                </p>
                                <RankBadge
                                    rank={placement?.placed_rank}
                                    size="lg"
                                    showLabel={false}
                                />
                                <p className="mt-4 text-gray-600 dark:text-neutral-300">
                                    {t('evaluation.placement.resultSubtitle')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Result Status */}
                    {!isPlacement && (
                        <div
                            className={`border-b border-white/30 px-8 py-6 dark:border-white/10 ${
                                results.passed ? 'bg-green-50' : 'bg-red-50'
                            }`}
                        >
                            <div className="text-center">
                                {results.passed ? (
                                    <div>
                                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500">
                                            <svg
                                                className="h-8 w-8 text-white"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                        <h2 className="mb-2 text-2xl font-bold text-green-800">
                                            {t('evaluation.results.passed')}
                                        </h2>
                                        <p className="text-green-700">
                                            {t(
                                                'evaluation.results.congratulations'
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500">
                                            <svg
                                                className="h-8 w-8 text-white"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                        <h2 className="mb-2 text-2xl font-bold text-red-800">
                                            {t('evaluation.results.failed')}
                                        </h2>
                                        <p className="text-red-700">
                                            {t('evaluation.results.tryAgain')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Score Display */}
                    <div className="border-b border-white/30 px-8 py-6 dark:border-white/10">
                        <div className="text-center">
                            <div
                                className={`mb-2 text-6xl font-bold ${getScoreColor()}`}
                            >
                                {Math.round(results.percentage)}%
                            </div>
                            <p className="mb-4 text-gray-600 dark:text-neutral-300">
                                {t('evaluation.results.finalScore')}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-neutral-400">
                                {t('evaluation.results.scoreDetails', {
                                    score: results.score,
                                    total: results.total_score,
                                    passing: evaluation.passing_score,
                                })}
                            </p>
                        </div>
                    </div>

                    {/* Detailed Statistics */}
                    <div className="border-b border-white/30 px-8 py-6 dark:border-white/10">
                        <h3 className="mb-6 text-lg font-semibold text-gray-800 dark:text-white">
                            {t('evaluation.results.detailedResults')}
                        </h3>

                        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                            <div className="text-center">
                                <div className="mb-1 text-2xl font-bold text-green-600">
                                    {results.correct_answers}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-neutral-400">
                                    {t('evaluation.results.correct')}
                                </p>
                            </div>

                            <div className="text-center">
                                <div className="mb-1 text-2xl font-bold text-red-600">
                                    {results.incorrect_answers}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-neutral-400">
                                    {t('evaluation.results.incorrect')}
                                </p>
                            </div>

                            <div className="text-center">
                                <div className="mb-1 text-2xl font-bold text-gray-600 dark:text-neutral-300">
                                    {results.unanswered_questions}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-neutral-400">
                                    {t('evaluation.results.unanswered')}
                                </p>
                            </div>

                            <div className="text-center">
                                <div className="mb-1 text-2xl font-bold text-gray-800 dark:text-white">
                                    {results.total_questions}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-neutral-400">
                                    {t('evaluation.results.total')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Answer Review */}
                    {questions.length > 0 && (
                        <div className="border-b border-white/30 px-8 py-6 dark:border-white/10">
                            <h3 className="mb-6 text-lg font-semibold text-gray-800 dark:text-white">
                                {t('evaluation.results.answerReviewTitle')}
                            </h3>
                            <EvaluationAnswersReview questions={questions} />
                        </div>
                    )}

                    {/* Additional Info */}
                    <div className="border-b border-white/30 px-8 py-6 dark:border-white/10">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <div>
                                <h4 className="mb-2 font-medium text-gray-800 dark:text-white">
                                    {t('evaluation.results.timeTaken')}
                                </h4>
                                <p className="text-gray-600 dark:text-neutral-300">
                                    {formatTime(results.time_taken_minutes)}
                                </p>
                            </div>

                            <div>
                                <h4 className="mb-2 font-medium text-gray-800 dark:text-white">
                                    {t('evaluation.results.submittedAt')}
                                </h4>
                                <p className="text-gray-600 dark:text-neutral-300">
                                    {formatDate(results.submitted_at)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Retake Info */}
                    {!results.passed && !isPlacement && (
                        <div className="border-b border-white/30 px-8 py-6 dark:border-white/10">
                            <div className="text-center">
                                {can_retake ? (
                                    <div className="bg-theme-light rounded-lg p-6">
                                        <h3 className="text-theme-text mb-2 font-semibold">
                                            {t(
                                                'evaluation.results.retakeAvailable'
                                            )}
                                        </h3>
                                        <p className="text-theme-text mb-4 text-sm">
                                            {t(
                                                'evaluation.results.retakeMessage'
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    next_retake_time && (
                                        <div className="rounded-lg bg-yellow-50 p-6">
                                            <h3 className="mb-2 font-semibold text-yellow-800">
                                                {t(
                                                    'evaluation.results.retakeCooldown'
                                                )}
                                            </h3>
                                            <p className="text-sm text-yellow-700">
                                                {t(
                                                    'evaluation.results.retakeAvailableIn',
                                                    {
                                                        time: formatRetakeTime(),
                                                    }
                                                )}
                                            </p>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {/* Certificate Section */}
                    {results.passed &&
                        has_certificate_template &&
                        !isPlacement && (
                            <div className="border-b border-white/30 px-8 py-6 dark:border-white/10">
                                <div className="text-center">
                                    <div className="rounded-lg bg-green-50 p-6">
                                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500">
                                            <svg
                                                className="h-8 w-8 text-white"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                        </div>
                                        <h3 className="mb-2 font-semibold text-green-800">
                                            {t('evaluation.certificate.title')}
                                        </h3>
                                        <p className="mb-4 text-sm text-green-700">
                                            {t(
                                                'evaluation.certificate.description'
                                            )}
                                        </p>
                                        <PrimaryButton
                                            onClick={handleViewCertificate}
                                            size="md"
                                        >
                                            {t(
                                                'evaluation.certificate.viewButton'
                                            )}
                                        </PrimaryButton>
                                    </div>
                                </div>
                            </div>
                        )}

                    {/* Action Buttons */}
                    <div className="px-8 py-6">
                        <div className="flex flex-col justify-center gap-3 sm:flex-row">
                            {isPlacement ? (
                                <PrimaryButton
                                    onClick={handleContinueToCourses}
                                    size="md"
                                >
                                    {t(
                                        'evaluation.placement.continueToCourses'
                                    )}
                                </PrimaryButton>
                            ) : (
                                <>
                                    {!results.passed && can_retake && (
                                        <PrimaryButton
                                            onClick={handleRetakeEvaluation}
                                            size="md"
                                        >
                                            {t(
                                                'evaluation.results.retakeEvaluation'
                                            )}
                                        </PrimaryButton>
                                    )}

                                    <SecondaryButton
                                        onClick={handleReturnToCourse}
                                        size="md"
                                    >
                                        {t('evaluation.results.returnToCourse')}
                                    </SecondaryButton>
                                </>
                            )}
                        </div>
                    </div>
                </GlassCard>
            </div>
        </AppLayout>
    );
};

const Results: React.FC<ResultsProps> = (props) => {
    return (
        <ThemeWrapper>
            <ResultsContent {...props} />
        </ThemeWrapper>
    );
};

export default Results;
